import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  IDeliveryBatch,
  IOrderDeliveryTask,
} from '../../../interfaces/delivery-company/tasks';
import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
} from 'axios';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { API_ENDPOINT } from '../../../configurations/global.config';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  Typography,
} from '@mui/material';
import { isMobile, isTablet } from 'react-device-detect';
import FilterBar from '../../common/filter-bar/filter-bar';
import { IFilterOption } from '../../common/filter-bar/types';
import { useSearchParams } from 'react-router-dom';
import {
  ALL,
  AWAITING_COLLECTION,
  FINISHED,
  IN_PROGRESS,
  PENDING,
  TASK_STATE_CATEGORY,
} from './filter_utils';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import DeliveryCardListing from './delivery-tasks-listing';
import { result } from 'lodash';
import { DriverRowsPerPage } from '../driver-constant';
import { buildQueryParams } from '../../common/filter-bar/filter-bar.utils';

export enum ListType {
  Batch,
  Task,
}

function DriverDeliveryBatches() {
  const [listType, setListType] = useState<ListType>(ListType.Batch);
  const [deliveryBatches, setDeliveryBatches] = useState<IDeliveryBatch[]>([]);
  const [deliveryTasks, setDeliveryTasks] = useState<IOrderDeliveryTask[]>([]);
  const [listCount, setListTasksCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [filters, setFilters] = useState<IFilterOption[]>([]);
  const { user, config } = useContext(AuthContext);
  const [refreshKey, setRefreshKey] = useState(0);
  const [criticalOnly, setCriticalOnly] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const taskStateFilter = (searchParams.get('status') ||
    AWAITING_COLLECTION) as TASK_STATE_CATEGORY;

  const changeStateCategory = (newTarget: TASK_STATE_CATEGORY) => {
    setSearchParams({ status: newTarget });
  };

  const batch_relevant_fields = [
    'id',
    'creation_date',
    'status',
    'driver',
    'package_count',
  ];

  function getEndpoint() {
    if (taskStateFilter === AWAITING_COLLECTION) {
      return `${API_ENDPOINT}/api/v1/delivery-batches/?page_size=${controller.rowsPerPage}&status=${taskStateFilter}&fields=${batch_relevant_fields.join(',')}`;
    } else {
      return `${API_ENDPOINT}/api/v1/delivery-tasks/?page_size=${controller.rowsPerPage}&status=${taskStateFilter}`;
    }
  }

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 10,
    searchColumn: '',
    searchValue: '',
    searchOperation: 'equals',
  });

  const resetPaginationControls = () => {
    setController({
      ...controller,
      page: 0,
    });
  };
  const filterQueryAction = (filters: IFilterOption[]) => {
    resetPaginationControls();
    setFilters(filters);
  };

  const loadMoreTasks = () => {
    setController((prev) => ({
      ...prev,
      rowsPerPage: prev.rowsPerPage + DriverRowsPerPage,
    }));
  };

  const cancelTokenSourceRef = useRef<CancelTokenSource | null>(null);

  const getAndSetTasks = async () => {
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel(
        'Operation canceled due to new request'
      );
    }

    // Create new cancel token
    const cancelTokenSource = axios.CancelToken.source();
    cancelTokenSourceRef.current = cancelTokenSource;

    setIsLoading(true);
    setMessage('');
    setResponse(undefined);
    let endpoint = getEndpoint();
    if (criticalOnly) endpoint += '&is_critical=True';
    endpoint += buildQueryParams(filters);
    console.log(endpoint);
    try {
      const requestConfig: AxiosRequestConfig = {
        ...config,
        cancelToken: cancelTokenSource.token,
      };

      let response;
      if (taskStateFilter == AWAITING_COLLECTION) {
        response = await axios.get<{
          results: IDeliveryBatch[];
          count: number;
        }>(endpoint, requestConfig);
        console.log(response);
        setDeliveryBatches(response.data.results);
        setListType(ListType.Batch);
      } else {
        response = await axios.get<{
          results: IOrderDeliveryTask[];
          count: number;
        }>(endpoint, config);
        setListType(ListType.Task);
        setDeliveryTasks(response.data.results);
      }
      setListTasksCount(response.data.count);
      setResponse(response);
      console.log(response);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setMessage(
          'Failed to get your delivery batches or orders: ' + e.response?.status
        );
        setResponse(e.response);
      } else setMessage('Failed to get your delivery batches or orders');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getAndSetTasks();
    return () => {
      if (cancelTokenSourceRef.current) {
        cancelTokenSourceRef.current.cancel('Component unmounted');
      }
    };
  }, [controller, taskStateFilter, refreshKey, criticalOnly, filters]);
  return (
    <>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 0, mb: 0 }}
      >
        Delivery Tasks
      </Typography>
      {taskStateFilter != AWAITING_COLLECTION && (
        <>
          <FilterBar
            filterQueryAction={filterQueryAction}
            currentlySelectedFilters={filters}
            filterKeys={['merchant', 'customer', 'district']}
            headless
          />
          <br />
        </>
      )}

      <div style={{ textAlign: 'center', width: '100%' }}>
        <Switch
          checked={criticalOnly}
          onChange={() => setCriticalOnly(!criticalOnly)}
        ></Switch>
        Critical Tasks Only
      </div>

      <FormControl component="fieldset" sx={{ marginBottom: 2 }} fullWidth>
        <RadioGroup
          row
          aria-label="state"
          name="state"
          value={taskStateFilter}
          onChange={(e) =>
            changeStateCategory(e.target.value as TASK_STATE_CATEGORY)
          }
          sx={{ display: 'flex', gap: 0, justifyContent: 'center' }}
        >
          <FormControlLabel
            value={AWAITING_COLLECTION}
            control={<Radio size="small" />}
            label="Awaiting Collection"
          />
          <FormControlLabel
            value={PENDING}
            control={<Radio size="small" />}
            label="Pending"
          />
          <FormControlLabel
            value={IN_PROGRESS}
            control={<Radio size="small" />}
            label="In Progress"
          />
          <FormControlLabel
            value={FINISHED}
            control={<Radio size="small" />}
            label="Finished"
          />
          <FormControlLabel
            value={ALL}
            control={<Radio size="small" />}
            label="All"
          />
        </RadioGroup>
      </FormControl>

      <DeliveryCardListing
        listType={listType}
        isLoading={isLoading}
        batches={deliveryBatches}
        tasks={deliveryTasks}
        statusFilter={taskStateFilter}
        reloadList={getAndSetTasks}
        loadMoreTasks={loadMoreTasks}
        totalCount={listCount}
        changeStateCategory={changeStateCategory}
        updateTasks={(
          mutator: (task: IOrderDeliveryTask) => IOrderDeliveryTask
        ) => setDeliveryTasks(deliveryTasks.map((p) => mutator(p)))}
      />
    </>
  );
}

export default DriverDeliveryBatches;
